import { publish } from '~/event-bus';
import { formatMoney, getCartSectionNames } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

if (!customElements.get('bundle-form'))
  customElements.define(
    'bundle-form',
    class BundleForm extends HTMLCustomElement {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.on('click', this.targets.submit, this.handleSubmitEvent.bind(this));
        this.on('quantity', this.parts, this.setFormState.bind(this));

        requestAnimationFrame(() => {
          this.parts.forEach((part) => {
            /**
             * The visual selector is used to get around an issue with the `hidden` attribute on options on iOS not hiding.
             */
            if (!part.targets['visual-selector']) return;

            this.on('change', part.targets['visual-selector'], () => {
              /**
               * Sync the visual selector with it's hidden version.
               */
              part.targets.selector.value = part.targets['visual-selector'].value;

              part.targets.selector.dispatchEvent(new Event('change'));
            });

            /**
             * Set the visual selectors' default states.
             */
            [...part.targets.selector.options].forEach((option) => {
              if (!option.hidden) {
                part.targets['visual-selector'].appendChild(option.cloneNode(true));
              }
            });

            part.targets['visual-selector'].dispatchEvent(new Event('change'));
          });
        });

        this.on('change', this.targets['us-size-type-toggle'], (event) => {
          const type = event.target.checked ? 'US' : 'UK';

          this.parts.forEach((part) => {
            if (part.targets.selector.hidden) return;

            /**
             * Clean up the visual selector ready for the new state of options.
             */
            part.targets['visual-selector'].innerHTML = '';

            [...part.targets.selector.options].forEach((option) => {
              const hidden = !option.dataset.sizeTypes.includes(type);

              option.hidden = hidden;

              if (!hidden) {
                /**
                 * If the option is visible, add it to the visual selector.
                 */
                const clone = option.cloneNode(true);

                clone.hidden = false;

                part.targets['visual-selector'].appendChild(clone);
              }
            });

            const selected = part.targets.selector.options[part.targets.selector.selectedIndex];
            const firstVisibleOption = [...part.targets.selector.options].find(({ hidden }) => !hidden);

            if (
              firstVisibleOption &&
              ((selected && selected.hidden) || !part.targets.selector.value) &&
              selected.value !== firstVisibleOption.value
            ) {
              part.targets.selector.value = firstVisibleOption?.value;
              part.targets.selector.dispatchEvent(new Event('change'));
            }

            part.targets.selector.disabled = ![...part.targets.selector.options].find(({ hidden }) => !hidden);
          });
        });
      }

      setFormState() {
        if (this.hasSelectedParts) {
          /**
           * Enable the submit button.
           */
          this.targets.submit.disabled = false;
          this.targets.submit.innerHTML = theme.strings.sections.main_product.bundle.add_to_cart;

          /**
           * Update and show the total price.
           */
          this.targets.total.classList.remove('display-none');

          if (window.is_wcp_wholesale_customer) {
            setTimeout(() => {
              this.targets.total.innerHTML = this.wpdUpdatePrice(this);
            }, 200);
          } else {
            this.targets.total.innerHTML = formatMoney(
              this.parts.reduce((total, { price, quantity }) => total + price * quantity, 0)
            );
          }

          this.targets.from.classList.add('display-none');
          return;
        }

        /**
         * Disable the submit button.
         */
        this.targets.submit.disabled = true;
        this.targets.submit.innerHTML = theme.strings.sections.main_product.bundle.select_products;

        /**
         * Hide the total price, show the "from" price.
         */
        this.targets.total.classList.add('display-none');
        this.targets.from.classList.remove('display-none');
      }

      /**
       * Updates the wholesale pricing.
       * - Code added by the Wholesale Helper app.
       * @param {BundleForm} form
       */
      wpdUpdatePrice(form) {
        let total = 0,
          inclTotal = 0,
          exclTotal = 0;
        let priceHtml = '';
        form.querySelectorAll('quantity-selector input').forEach((qty) => {
          if (qty.value > 0) {
            let itemRow = qty.closest('bundle-part');
            let price = itemRow.querySelector('.wpd-processed');
            if (price) {
              let basePrice = price.querySelector('.wpd-ext-compare-at-price');
              let taxIncl = price.querySelector('.wpd-tax-incl-price');
              let taxExcl = price.querySelector('.wpd-tax-excl-price');

              total += parseInt(basePrice.innerText.match(/\d+/g).join('') * qty.value);

              if (typeof wpdFormatePrice != 'function') {
                wpdFormatePrice = formatMoney;
              }

              priceHtml = `
                <s class="wpd-ext-compare-at-price">${wpdFormatePrice(total)}</s>
              `;

              if (taxIncl) {
                inclTotal += parseInt(taxIncl.innerText.match(/\d+/g).join('') * qty.value);
                priceHtml += `
                  <span class="wpd-tax-incl-price">${wpdFormatePrice(inclTotal)} in. VAT</span>
                  <br class="wpd-tax-excl-price-br">
                `;
              }
              if (taxExcl) {
                exclTotal += parseInt(taxExcl.innerText.match(/\d+/g).join('') * qty.value);
                priceHtml += `
                  <small class="wpd-tax-excl-price">(${wpdFormatePrice(exclTotal)} ex. VAT)</small>
                `;
              }
            }
          }
        });
        return priceHtml;
      }

      async handleSubmitEvent() {
        this.targets.submit.disabled = true;
        this.targets.submit.innerHTML = theme.strings.sections.main_product.adding_to_cart;

        await fetch(`${theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: this.parts
              .filter(({ quantity }) => quantity >= 1)
              .map(({ selected, quantity }) => ({ id: selected, quantity }))
              .reverse(),
            sections: getCartSectionNames(),
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            publish('cart:update', { sections: response.sections });
            publish('cart:added');

            this.targets.submit.disabled = false;
            this.targets.submit.innerHTML = theme.strings.sections.main_product.bundle.add_to_cart;
          });
      }

      get parts() {
        return [...this.querySelectorAll('bundle-part')];
      }

      get hasSelectedParts() {
        return this.parts.some(({ quantity }) => quantity >= 1);
      }
    }
  );

if (!customElements.get('bundle-part'))
  customElements.define(
    'bundle-part',
    class BundlePart extends HTMLCustomElement {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.on('change', this.targets.selector, this.handleSelectorChange.bind(this), null, true);
        this.on('change', this.targets.quantity.input, this.handleQuantityChange.bind(this));
      }

      handleQuantityChange() {
        this.dispatchEvent(new CustomEvent('quantity', { detail: this.quantity }));
      }

      handleSelectorChange(event) {
        const element = event ? event.target : this.targets.selector;

        if (element.selectedIndex >= 0) {
          const {
            dataset: { priceHtml, maxQuantity, price, value, wpdProdhandle, wpdCPrice },
          } = element.options[element.selectedIndex];

          this.targets.price.innerHTML = priceHtml;
          // console.log("element",  element.options[element.selectedIndex]);
          // console.log("priceHtml",   this.targets.price);
          setTimeout(() => {
            try {
              this.targets.price.classList.remove('wpd-processed');
              this.targets.price.setAttribute('data-wpd-variant-compare-at-price', wpdCPrice == null ? '' : wpdCPrice);
              this.targets.price.setAttribute('data-wpd-variant-price', price);
              this.targets.price.setAttribute('data-wpd-variant-id', event.target.value);
              this.targets.price.setAttribute('data-wpd-product-handle', wpdProdhandle);
            } catch (e) {}
          }, 0);
          if (maxQuantity) {
            this.targets.quantity.input.max = parseInt(maxQuantity);
          }
        }

        this.targets.quantity.setInputValue(0);
      }

      get quantity() {
        return parseInt(this.targets.quantity.input.value);
      }

      get selected() {
        return parseInt(this.targets.selector.value);
      }

      get price() {
        return (
          this.targets.selector.selectedIndex >= 0 &&
          parseInt(this.targets.selector.options[this.targets.selector.selectedIndex].dataset.price)
        );
      }
    }
  );
